import { useState } from 'react';
import { Star, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const topProducts = [
  {
    id: 1,
    name: 'Classic Shawarma',
    price: 80,
    image: 'https://images.unsplash.com/photo-1662116765994-1e4200c43589?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxzaGF3YXJtYXxlbnwwfHx8fDE3NzEwNjg1MTZ8MA&ixlib=rb-4.1.0&q=85',
    rating: 4.8,
    category: 'featured',
    veg: false
  },
  {
    id: 2,
    name: 'Peri Peri Shawarma',
    price: 100,
    image: 'https://images.unsplash.com/photo-1719282431565-3b30bb7d2658?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxzaGF3YXJtYXxlbnwwfHx8fDE3NzEwNjg1MTZ8MA&ixlib=rb-4.1.0&q=85',
    rating: 4.9,
    category: 'bestseller',
    veg: false
  },
  {
    id: 3,
    name: 'French Fries',
    price: 60,
    image: 'https://images.unsplash.com/photo-1630384060421-cb20d0e0649d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxmcmllc3xlbnwwfHx8fDE3NzEwNjg1Mzd8MA&ixlib=rb-4.1.0&q=85',
    rating: 4.6,
    category: 'featured',
    veg: true
  },
  {
    id: 4,
    name: 'Chocolate Shake',
    price: 60,
    image: 'https://images.unsplash.com/photo-1595434091143-b375ced5fe5c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHx0ZWElMjBjdXB8ZW58MHx8fHwxNzcxMDY4NTIwfDA&ixlib=rb-4.1.0&q=85',
    rating: 4.7,
    category: 'featured',
    veg: true
  },
  {
    id: 5,
    name: 'Blue Curacao Mojito',
    price: 80,
    image: 'https://images.unsplash.com/photo-1653542772393-71ffa417b1c4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxtb2ppdG98ZW58MHx8fHwxNzcxMDY4NTI5fDA&ixlib=rb-4.1.0&q=85',
    rating: 4.8,
    category: 'latest',
    veg: true
  },
  {
    id: 6,
    name: 'Chicken Nuggets',
    price: 80,
    image: 'https://images.unsplash.com/photo-1562967914-608f82629710?w=800',
    rating: 4.5,
    category: 'featured',
    veg: false
  },
  {
    id: 7,
    name: 'Kiwi Shake',
    price: 80,
    image: 'https://images.unsplash.com/photo-1610478506025-8110cc8f1986?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHx0ZWElMjBjdXB8ZW58MHx8fHwxNzcxMDY4NTIwfDA&ixlib=rb-4.1.0&q=85',
    rating: 4.6,
    category: 'latest',
    veg: true
  },
  {
    id: 8,
    name: 'Royal Falooda',
    price: 120,
    image: 'https://images.unsplash.com/photo-1577968897966-3d4325b36b61?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHw0fHx0ZWElMjBjdXB8ZW58MHx8fHwxNzcxMDY4NTIwfDA&ixlib=rb-4.1.0&q=85',
    rating: 4.9,
    category: 'bestseller',
    veg: true
  }
];

export const TopProducts = () => {
  const [activeFilter, setActiveFilter] = useState('featured');

  const filteredProducts = topProducts.filter(
    (product) => product.category === activeFilter
  );

  const handleWhatsAppOrder = (productName) => {
    const phoneNumber = '919994520043';
    const message = `Hi! I would like to order ${productName} from Insta Cafe.`;
    window.open(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
    toast.success(`Opening WhatsApp to order ${productName}`);
  };

  return (
    <section className="section-padding">
      <div className="section-container">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground mb-4">
            Top Products
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto mb-8">
            Our customers' favorite picks
          </p>

          {/* Filter Buttons */}
          <div className="flex flex-wrap justify-center gap-3">
            {['featured', 'latest', 'bestseller'].map((filter) => (
              <Button
                key={filter}
                variant={activeFilter === filter ? 'default' : 'outline'}
                onClick={() => setActiveFilter(filter)}
                className={activeFilter === filter ? 'btn-primary' : ''}
              >
                {filter.charAt(0).toUpperCase() + filter.slice(1)}
              </Button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {filteredProducts.map((product) => (
            <div key={product.id} className="product-card group">
              {/* Image Container */}
              <div className="relative h-56 overflow-hidden rounded-t-xl">
                <img
                  src={product.image}
                  alt={product.name}
                  className="product-image w-full h-full object-cover"
                />
                {/* Veg/Non-Veg Badge */}
                <div className="absolute top-3 left-3">
                  {product.veg ? (
                    <div className="veg-indicator" />
                  ) : (
                    <div className="non-veg-indicator" />
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="p-4 flex flex-col">
                <h3 className="font-semibold text-lg text-foreground mb-2">
                  {product.name}
                </h3>

                {/* Rating */}
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-4 h-4 ${i < Math.floor(product.rating)
                          ? 'fill-secondary text-secondary'
                          : 'text-muted'
                        }`}
                    />
                  ))}
                  <span className="text-sm text-muted-foreground ml-1">
                    {product.rating}
                  </span>
                </div>

                {/* Price & Action */}
                <div className="flex items-center justify-between mt-auto">
                  <span className="text-xl font-bold text-primary">
                    ₹{product.price}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    className="hover:bg-green-500 hover:text-white hover:border-green-500"
                    onClick={() => handleWhatsAppOrder(product.name)}
                  >
                    <MessageCircle className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TopProducts;